/**
 * feat-138 — HTML/CSS replica of RestaurantAI-Flutter/lib/widgets/
 * pairing_challenge_card.dart, for the editor's LIVE preview only.
 *
 * A faithful, DELIBERATELY TEMPORARY replica (ADR-0001 slices; feature-request
 * §5) — NOT a shared component. It renders a single derived `status` blob
 * (DerivedRow) exactly as the on-device card would, so a Bespoke admin can see
 * both locations' cards update as they type and catch a swapped goal / flipped
 * is_you before saving.
 *
 * feat-227 — SIMPLIFIED TO THE PERCENTAGES (request §4.1–§4.3, mirrored here per
 * §5/D9). Gone: the red header band, the PREVIOUS DAY / TODAY'S GOAL cells, the
 * caption, the pace pills, the dashed pace line, the cream body. What replaces
 * them: a sentence-case `title` heading and two percentage bars on a white
 * surface, with a gradient fill, a `bar_color`-tinted track, a bare mascot at the
 * fill head, and a 🏁 past the 100% end.
 *
 * THE EDITOR FORM STILL COLLECTS EVERY REMOVED FIELD, ON PURPOSE (D2): the
 * feat-140 recalc keeps computing and storing them daily and the write model still
 * requires them. Nothing was retired — this card just stopped drawing them. The
 * inputs are labeled "Stored, not shown on the app" in `PairingChallengeEditor`
 * so an author isn't left wondering why their edit never reaches the phone.
 *
 * MASCOTS ARE NOT THE MOCKUP'S (D11). `target-mockup.png` draws One California as
 * a pepper and 580 California as an avocado; the seeded data says avocado and
 * cabbage, and D11 leaves the assignment alone. AC9 asks this preview to match THE
 * PHONE, so it renders whatever `avatar_url`/`avatar_placeholder` carry. The
 * avocado/cabbage pairing is correct and expected — it is not a defect against the
 * mockup. The mockup is authoritative for layout, color, and mascot TREATMENT
 * (D10: bare art, larger, overflowing above the bar — no white circle badge), and
 * is NOT authoritative for which mascot belongs to which team.
 *
 * All per-team colors are derived from the authored `bar_color` in
 * `pairingBarColor.ts` — never hardcoded here. See that file for why, and for the
 * pixel measurements each rule came from.
 *
 * ⚠️ THIS PREVIEW'S WIDTH IS THE ADMIN PAGE'S, NOT THE DEVICE'S — DO NOT READ
 * ANYTHING WIDTH-DEPENDENT OFF IT. There is deliberately no card-width constant
 * here: the card is fluid and takes whatever the editor column gives it
 * (`PairingChallengeEditor.tsx:706,709`), which the page's `max-w-7xl` currently
 * settles at ~254 px, while the phone card is ~328 dp of content. So COLOR and
 * STRUCTURE in here are faithful and can be compared to the phone; heading wrap,
 * name ellipsis, the flag gutter, and D10's mascot overflow are NOT. Measured
 * 2026-08-08: "Perfect Pairing Challenge" is 232.6 px at this heading size, so it
 * wraps to two lines here and will not on the device. That is a frame artifact, not
 * a rendering discrepancy — do not "fix" the card to make it match.
 *
 * (Pinning the preview to a device-width frame is a separate ticket, raised by
 * design-lead; widening this slice's diff to chase it would be exactly the D1 drift
 * the spec's tripwire is about.)
 */
import React from 'react';
import type { DerivedRow, DerivedTeam } from './pairingDerivation';
import {
  barDotColor,
  barFillGradient,
  barTextColor,
  barTrackTint,
  emojiForPlaceholder,
} from './pairingBarColor';
import {
  CARD_PADDING,
  DOT_GAP,
  DOT_SIZE,
  FLAG_FONT_SIZE,
  FLAG_WIDTH,
  HEADING_BOTTOM_GAP,
  HEADING_FONT_SIZE,
  LABEL_TO_LANE_GAP,
  LANE_HEIGHT,
  MASCOT_SIZE,
  MASCOT_TOP,
  NAME_FONT_SIZE,
  PERCENT_FONT_SIZE,
  PERCENT_SUFFIX_FONT_SIZE,
  ROW_GAP,
  TRACK_HEIGHT,
  TRACK_TOP,
  YOU_FONT_SIZE,
  YOU_GAP,
  fillWidth,
  mascotLeft,
  trackWidth,
} from './pairingCardGeometry';

// ── BespokeTheme tokens (mirrored 1:1 from bespoke_theme.dart) ────────────────
// Only the surface-and-ink tokens survive feat-227. The per-team colors that used
// to sit here (`red`/`redDark` for the band, `border` for the flat grey track) are
// gone: the band is gone, and the track is now derived from `bar_color` (D7).
// The old `textMuted` was DUAL-ROLE — pace line AND `bar_color` fallback — and only
// the pace line is being removed; the fallback moved to `pairingBarColor.ts` as
// `BAR_COLOR_FALLBACK`, where it can only mean one thing.
const C = {
  surface: '#FFFFFF', // card body (D6 — white, replacing the cream `BespokeTheme.muted`)
  textPrimary: '#2C2C2C',
  textSecondary: '#6B6B6B',
};
const DISPLAY_FONT = "'Fredoka', 'Nunito', sans-serif";


// ── Mascot: remote url → emoji fallback (never blank) ─────────────────────────
// D10 retires the white circle badge, the 2.5 px white ring, and the drop shadow.
// The art is drawn bare, `contain`ed in a square box so a non-square remote image
// keeps its aspect ratio instead of being cropped by the old `cover`.
const PairingAvatar: React.FC<{ team: DerivedTeam }> = ({ team }) => {
  const [failed, setFailed] = React.useState(false);
  const url = team.avatar_url;
  const showImg = !!url && !failed;
  return (
    <div
      data-testid="pairing-mascot"
      style={{
        width: MASCOT_SIZE,
        height: MASCOT_SIZE,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      {showImg ? (
        <img
          src={url}
          alt=""
          width={MASCOT_SIZE}
          height={MASCOT_SIZE}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          onError={() => setFailed(true)}
        />
      ) : (
        <span style={{ fontSize: MASCOT_SIZE * 0.72, lineHeight: 1 }}>
          {emojiForPlaceholder(team.avatar_placeholder)}
        </span>
      )}
    </div>
  );
};

// ── One team's bar row (label row + track lane) ───────────────────────────────
const BarRow: React.FC<{ team: DerivedTeam; index: number }> = ({ team, index }) => {
  const dot = barDotColor(team.bar_color);
  const track = barTrackTint(team.bar_color);
  const fill = barFillGradient(team.bar_color);
  // One ink for every bar_color-derived glyph in the row — name, numeral, suffix.
  const teamInk = barTextColor(team.bar_color);

  // DISPLAY-ONLY INVARIANT (ADR 0024 D4): the card performs ZERO arithmetic on
  // challenge numbers. The geometry helpers clamp for LAYOUT ONLY — the label below
  // prints `pct` untouched, so a stored 104 prints "104%" and a stored 0 prints "0%".
  const pct = team.percent_to_goal;

  return (
    <div>
      {/* Label row. With the pace pill gone (§4.3 item 1) there is ample width, so
          the feat-129 name-vs-pill shrink negotiation — a `FitScaleRight` wrapper,
          a ResizeObserver, and a 0.40 scale floor — is deleted rather than left to
          rot around a pill that no longer exists. The name simply takes the width
          it needs and the percent is pinned right. */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span
          data-testid={`pairing-dot-${index}`}
          style={{
            width: DOT_SIZE,
            height: DOT_SIZE,
            borderRadius: '50%',
            background: dot,
            flex: '0 0 auto',
          }}
        />
        <span style={{ width: DOT_GAP, flex: '0 0 auto' }} />
        <span
          style={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: NAME_FONT_SIZE,
            fontWeight: 700,
            // Team-colored, matching the mockup and the Flutter slice. This 15 px
            // bold name is the SMALLEST bar_color text on the card and therefore the
            // element that set the floor: bold below 18.66 px is not "large text", so
            // it is held to 4.5:1, not the numeral's 3.0. `barTextColor` guarantees
            // that for every team — the seeded red passes untouched at 6.24:1 and the
            // seeded gold is taken to #9B6E00 at 4.54:1. Ruled by design-lead
            // 2026-08-08; see TEAM_TEXT_CONTRAST_FLOOR.
            color: teamInk,
          }}
        >
          {team.name}
        </span>
        {team.is_you && (
          <span
            style={{
              marginLeft: YOU_GAP,
              fontSize: YOU_FONT_SIZE,
              fontWeight: 600,
              // Stays neutral — it is a marker, not part of the team's identity.
              color: C.textSecondary,
              flex: '0 0 auto',
            }}
          >
            (you)
          </span>
        )}
        <span style={{ flex: '1 1 auto', minWidth: 8 }} />
        <span
          data-testid={`pairing-percent-${index}`}
          style={{
            flex: '0 0 auto',
            fontFamily: DISPLAY_FONT,
            fontWeight: 800,
            // D5 — the team's own color, darkened only if it cannot clear the
            // contrast floor on white. See `barTextColor`.
            color: teamInk,
            lineHeight: 1,
          }}
        >
          <span style={{ fontSize: PERCENT_FONT_SIZE }}>{pct == null ? '—' : pct}</span>
          {pct != null && (
            // Same ink as the numeral, NOT a paler tint: the tint failed every floor
            // for every team and was rejected, so hierarchy comes from size alone.
            <span style={{ fontSize: PERCENT_SUFFIX_FONT_SIZE }}>%</span>
          )}
        </span>
      </div>
      <div style={{ height: LABEL_TO_LANE_GAP }} />
      {/* Track lane. NOT `overflow: hidden` — the mascot is meant to escape it
          upward (D10), and every ancestor up to the card body has to let it. */}
      <div style={{ position: 'relative', height: LANE_HEIGHT }}>
        <div
          data-testid={`pairing-track-${index}`}
          style={{
            position: 'absolute',
            top: TRACK_TOP,
            left: 0,
            width: trackWidth(),
            height: TRACK_HEIGHT,
            // D7 — a pale tint of the team's OWN color, not a flat grey.
            background: track,
            borderRadius: TRACK_HEIGHT / 2,
          }}
        />
        <div
          data-testid={`pairing-fill-${index}`}
          style={{
            position: 'absolute',
            top: TRACK_TOP,
            left: 0,
            // The gradient is painted on THIS element, whose width is the fill, so
            // it reaches full `bar_color` at the fill head at any percent.
            width: fillWidth(pct),
            height: TRACK_HEIGHT,
            // `backgroundImage`, not the `background` shorthand: jsdom's parser
            // discards a gradient assigned to the shorthand, which would have made
            // every color assertion below untestable.
            backgroundImage: fill,
            borderRadius: TRACK_HEIGHT / 2,
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: MASCOT_TOP,
            // Centered on the fill head, kept inside the TRACK (not the lane) so it
            // never wanders into the flag's reserved column at 100%+.
            left: mascotLeft(pct),
          }}
        >
          <PairingAvatar team={team} />
        </div>
        <span
          data-testid={`pairing-finish-flag-${index}`}
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: TRACK_TOP + TRACK_HEIGHT / 2,
            right: 0,
            width: FLAG_WIDTH,
            transform: 'translateY(-50%)',
            fontSize: FLAG_FONT_SIZE,
            lineHeight: 1,
            textAlign: 'center',
          }}
        >
          🏁
        </span>
      </div>
    </div>
  );
};

/**
 * The card. Renders NOTHING for a malformed row — mirrors the Flutter whole-card
 * degrade (ADR 0024 D8).
 *
 * THE `header` CLAUSE IS GONE ON PURPOSE, TRACKING ADR 0001. This gate used to read
 * `!row?.header || teams empty`, mirroring Flutter's
 * `!isSupportedSchemaVersion || !hasHeader || teams.isEmpty`
 * (`challenge_status.dart:458`). feat-227's §4.5 gate ruled Option A and dropped
 * `!hasHeader` from the Flutter model, so it drops here too: after this ticket no
 * header field is drawn, and a gate on an undrawn field is the silent-vanish shape
 * the ADR removed. Had it stayed here only, the preview would disagree with the
 * phone about which payloads render at all — the one thing a replica must not do.
 * Schema version needs no check: `DerivedRow.schema_version` is the literal `2`.
 */
const PairingCardPreview: React.FC<{ row: DerivedRow }> = ({ row }) => {
  const malformed = !row || !Array.isArray(row.teams) || row.teams.length === 0;
  if (malformed) return null;
  const title = row.title ?? '';
  return (
    <div
      data-testid="pairing-challenge-card"
      style={{
        background: C.surface,
        borderRadius: 20,
        boxShadow: '0 6px 14px rgba(0,0,0,0.086)',
        padding: CARD_PADDING,
        // NOT `overflow: hidden` — the retired header band needed clipping to keep
        // its red corners inside the radius; nothing bleeds to the edge now, and
        // clipping here would crop the mascots that D10 pushes above their bars.
      }}
    >
      {title !== '' && (
        <div
          data-testid="pairing-title"
          style={{
            fontFamily: DISPLAY_FONT,
            fontSize: HEADING_FONT_SIZE,
            fontWeight: 700,
            color: C.textPrimary,
            // Sentence case AS AUTHORED (D4) — no `textTransform`. The retired band
            // uppercased this; the string now renders exactly as the editor stores
            // it, which is why the runbook re-authors it to "Perfect Pairing
            // Challenge" rather than any code change producing that heading.
            marginBottom: HEADING_BOTTOM_GAP,
          }}
        >
          {title}
        </div>
      )}
      {row.teams.map((team, i) => (
        <div key={team.location_id} style={{ marginTop: i === 0 ? 0 : ROW_GAP }}>
          <BarRow team={team} index={i} />
        </div>
      ))}
    </div>
  );
};

export default PairingCardPreview;
